import {useCallback} from 'react';
import {toast} from 'sonner';
import {Navigate, useNavigate} from 'react-router-dom';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {InstantCheckInFormTypes as FormTypes} from 'pages/Auth/types';
import {PATHS} from 'routes/constants';
import {buildStateFormData} from '../../utils';
import {isAdult} from 'utils/common';
import {useFetchParticipants} from 'hooks/auth/useFetchParticipants';
import {usePath} from 'hooks/common';
import {useContracts, useGuestGroup} from 'hooks';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useOnboardingStore} from '../../store/useOnboardingStore';
import {useGuestCreationContext} from 'pages/Auth/context';
import {useValidateInstantChekinFlow} from 'pages/Auth/hooks/useValidateInstantChekinFlow';
import {SelectGuestList} from '../../components/SelectGuestList';
import {StepInfoBox} from 'pages/Auth/components/StepInfoBox';
import {AgreementClausesBox} from 'components/AgreementClausesBox';
import {ContractBox} from 'components/SignBlock/ContractBox';
import {StyledButton, Wrapper} from './styled';
import {GUEST_FORM_NAMES} from '@chekin/core';

function WhoIsComingStep() {
  const {t} = useTranslation();
  const {data: participants} = useFetchParticipants();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {data: reservation} = useReservation();
  const {agreementClauses: clauses, formFields} = useSchema();
  const {guestGroup, leaderGuest} = useGuestGroup();
  const {contracts, isContractsError, isLoadingContracts} = useContracts();
  const defaultSelectedParticipants = useOnboardingStore(state => state.participants);
  const updateState = useOnboardingStore(state => state.updateState);
  const {validateFlow} = useValidateInstantChekinFlow();

  const formMethods = useForm<FormTypes>({
    defaultValues: {
      participants: defaultSelectedParticipants,
    },
  });
  const {handleSubmit, control} = formMethods;

  const {createGuests, isProcessing} = useGuestCreationContext();

  const skipInstantChekin = useCallback(() => {
    navigate(getFullPath(PATHS.home), {
      replace: true,
    });
  }, [navigate, getFullPath]);

  const onSubmit = (data: FormTypes) => {
    if (!data.participants?.length) {
      skipInstantChekin();
      return;
    }

    const {isValid, error} = validateFlow(data.participants);
    if (!isValid) {
      toast.error(error);
      return;
    }
    const isAnyParticipantAnAdult = data.participants.some(participant =>
      isAdult(participant.birth_date),
    );

    const stateFormData = buildStateFormData(data.participants, clauses, data);

    updateState(stateFormData);

    if (!leaderGuest && isAnyParticipantAnAdult) {
      navigate(getFullPath(PATHS.onboarding.confirmLeaderGuest));
      return;
    }

    void createGuests({
      customParticipants: data.participants,
      guestsData: stateFormData.guestsData,
    });
  };

  if (!participants?.length) {
    return <Navigate to={getFullPath(PATHS.auth.welcome)} replace />;
  }

  return (
    <Wrapper>
      <StepInfoBox
        title={t('who_is_coming')}
        description={t('select_the_guest_included_in_this_reservation')}
      />
      <FormProvider {...formMethods}>
        <Controller
          name="participants"
          control={control}
          render={({field: {value, ...field}}) => (
            <SelectGuestList
              participants={participants || []}
              guests={guestGroup?.members || []}
              value={value || []}
              {...field}
            />
          )}
        />

        {!!clauses?.length && <AgreementClausesBox clauses={clauses} />}
        <ContractBox
          contracts={contracts}
          hasError={isContractsError}
          isLoading={isLoadingContracts}
          isAutomatedSignature={
            formFields?.[GUEST_FORM_NAMES.signature] && reservation?.auto_signature
          }
        />
      </FormProvider>
      <StyledButton
        onClick={handleSubmit(onSubmit)}
        label={t('confirm')}
        disabled={isProcessing}
      />
    </Wrapper>
  );
}

export {WhoIsComingStep};
